import { useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaCheck, FaSearch } from 'react-icons/fa';
import { FieldShell } from './FieldShell';
import { useDomFieldName, useDomFieldId } from './_useFieldName';
import { FIELD_AUTOCOMPLETE_OFF } from './_fieldDefaults';

const BLOCK = 'LStudio__app_field';

/*
    🔥 SELECT — DESPLEGABLE PROPIO CON BUSCADOR

    ⚠️ POR QUÉ NO USAMOS EL <select> NATIVO

    Su desplegable lo dibuja el SISTEMA OPERATIVO, no el navegador:
    el resaltado azul, la barra de scroll y la tipografía los decide
    Windows. Con `color-scheme` solo se le puede decir claro u oscuro.

    ⚠️ BUSCADOR AUTOMÁTICO

    Aparece solo cuando hay muchas opciones (por defecto, 8 o más).
    Con cinco opciones un buscador estorba; con veinticuatro
    provincias, sin buscador el usuario scrollea a ciegas.

    Cuando NO hay buscador, funciona el tipeo rápido del select
    nativo: escribís "cor" y salta a Corrientes.

    ⚠️ LA BÚSQUEDA IGNORA ACENTOS
       Escribir "neuquen" tiene que encontrar "Neuquén". Si no, el
       buscador falla justo en los casos donde más se necesita.

    El <select> real queda oculto para el submit, la validación del
    navegador y los lectores de pantalla.
*/

const onNormalize = (text = '') => (
    String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
);

const onFlatten = (options) => options.flatMap((opt) => (opt.options ?? [opt]));

/* 🔥 Resalta el pedazo que coincide con lo buscado */
const Highlight = ({ text, query }) => {

    if (!query) return text;

    const NORM = onNormalize(text);
    const POS = NORM.indexOf(onNormalize(query));

    if (POS === -1) return text;

    return (
        <>
            {text.slice(0, POS)}
            <mark className={`${BLOCK}__option_match`}>{text.slice(POS, POS + query.length)}</mark>
            {text.slice(POS + query.length)}
        </>
    );
};

export const FieldSelect = ({
    id, name, label, hint, error,
    value, onChange, options = [], placeholder = 'Seleccionar…',
    isSearchable = null,              // null = automático
    searchThreshold = 8,
    searchPlaceholder = 'Buscar…',
    emptyText = 'Sin resultados',
    isNative = false,
    isBare = false,
    isRequired = false, isDisabled = false,
    span = 1, ...rest
}) => {

    const DOM_ID = useDomFieldId(id);

    const DOM_NAME = useDomFieldName();

    const REF__useRefBox = useRef(null);
    const REF__useRefList = useRef(null);
    const REF__useRefSearchInput = useRef(null);
    const REF__useRefButton = useRef(null);
    const REF__useRefTypeAhead = useRef('');
    const REF__useRefTimer = useRef(null);
    const REF__useRefInstanceId = useRef(`field_select_${Math.random().toString(36).slice(2)}`);

    const [useIsOpen, setIsOpen] = useState(false);
    const [useIndex, setIndex] = useState(-1);
    const [useQuery, setQuery] = useState('');

    const MEMO__FLAT_ALL = useMemo(() => onFlatten(options), [options]);

    const SHOW_SEARCH = isSearchable ?? (MEMO__FLAT_ALL.length >= searchThreshold);

    /* 🔥 Opciones filtradas, conservando los grupos */
    const MEMO__FILTERED = useMemo(() => {

        if (!useQuery) return options;

        const MATCH = (opt) => onNormalize(opt.label).includes(onNormalize(useQuery));

        return options
            .map((group) => {
                if (!group.options) return MATCH(group) ? group : null;
                const CHILDREN = group.options.filter(MATCH);
                return CHILDREN.length ? { ...group, options: CHILDREN } : null;
            })
            .filter(Boolean);

    }, [options, useQuery]);

    const MEMO__FLAT = useMemo(() => onFlatten(MEMO__FILTERED), [MEMO__FILTERED]);

    const SELECTED = MEMO__FLAT_ALL.find((opt) => String(opt.value) === String(value ?? ''));

    const onOpenSelect = () => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('lstudio:field-popover-open', { detail: REF__useRefInstanceId.current }));
        }
        setIsOpen(true);
    };

    const onToggleSelect = () => {
        if (useIsOpen) {
            setIsOpen(false);
            return;
        }
        onOpenSelect();
    };

    // Solo un dropdown puede quedar abierto a la vez. Evita select superpuestos.
    useEffect(() => {
        const onOtherSelectOpen = (event) => {
            if (event?.detail !== REF__useRefInstanceId.current) setIsOpen(false);
        };
        window.addEventListener('lstudio:field-popover-open', onOtherSelectOpen);
        return () => window.removeEventListener('lstudio:field-popover-open', onOtherSelectOpen);
    }, []);

    // 🔥 Cierra al clickear afuera
    useEffect(() => {

        const onClickOutside = (event) => {
            if (REF__useRefBox.current && !REF__useRefBox.current.contains(event.target)) setIsOpen(false);
        };

        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);

    }, []);

    // 🔥 Al abrir: limpia la búsqueda, marca el elegido y enfoca el buscador
    useEffect(() => {

        if (!useIsOpen) {
            setQuery('');
            return;
        }

        const POS = MEMO__FLAT_ALL.findIndex((opt) => String(opt.value) === String(value ?? ''));
        setIndex(POS);

        requestAnimationFrame(() => {

            if (SHOW_SEARCH) REF__useRefSearchInput.current?.focus();

            /*
                ⚠️ block: 'center' mueve TODOS los contenedores con scroll
                de arriba, no solo la lista: la pagina salta al tope y el
                cuerpo del modal se corre. Con 'nearest' solo se mueve la
                lista, y solo si la opcion no se ve.
            */
            const OPTION = REF__useRefList.current?.querySelector('[data-selected="true"]');

            if (OPTION) OPTION.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });

    }, [useIsOpen]);   // eslint-disable-line react-hooks/exhaustive-deps

    // 🔥 Al filtrar, el marcado vuelve al primer resultado
    useEffect(() => {
        if (useQuery) setIndex(MEMO__FLAT.length ? 0 : -1);
    }, [useQuery, MEMO__FLAT.length]);

    const onScrollToIndex = (pos) => requestAnimationFrame(() => {
        REF__useRefList.current
            ?.querySelectorAll(`.${BLOCK}__option`)[pos]
            ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });

    const onPick = (optValue) => {
        onChange?.({ target: { name: name ?? id, value: optValue } });
        setIsOpen(false);
        REF__useRefButton.current?.focus();
    };

    const onMove = (delta) => {

        if (!MEMO__FLAT.length) return;

        let pos = useIndex;

        // Salta los deshabilitados sin quedarse en bucle
        for (let step = 0; step < MEMO__FLAT.length; step += 1) {
            pos += delta;
            if (pos < 0) pos = MEMO__FLAT.length - 1;
            if (pos >= MEMO__FLAT.length) pos = 0;
            if (!MEMO__FLAT[pos]?.isDisabled) break;
        }

        setIndex(pos);
        onScrollToIndex(pos);
    };

    /*
        🔥 TIPEO RÁPIDO — solo cuando NO hay buscador

        Es lo que hace el select nativo: escribís "cor" y salta a
        Corrientes. La gente lo usa sin darse cuenta.
    */
    const onTypeAhead = (char) => {

        clearTimeout(REF__useRefTimer.current);

        REF__useRefTypeAhead.current += char.toLowerCase();

        REF__useRefTimer.current = setTimeout(() => { REF__useRefTypeAhead.current = ''; }, 700);

        const POS = MEMO__FLAT.findIndex((opt) => (
            onNormalize(opt.label).startsWith(onNormalize(REF__useRefTypeAhead.current))
        ));

        if (POS === -1) return;

        setIndex(POS);

        if (!useIsOpen) return onPick(MEMO__FLAT[POS].value);

        onScrollToIndex(POS);
    };

    const onKeyDown = (event) => {

        if (isDisabled) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!useIsOpen) return onOpenSelect();
            return onMove(1);
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!useIsOpen) return onOpenSelect();
            return onMove(-1);
        }

        if (event.key === 'Home') { event.preventDefault(); setIndex(0); return onScrollToIndex(0); }

        if (event.key === 'End') {
            event.preventDefault();
            setIndex(MEMO__FLAT.length - 1);
            return onScrollToIndex(MEMO__FLAT.length - 1);
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            if (!useIsOpen) return onOpenSelect();
            if (useIndex >= 0 && !MEMO__FLAT[useIndex]?.isDisabled) onPick(MEMO__FLAT[useIndex].value);
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpen(false);
            REF__useRefButton.current?.focus();
            return;
        }

        if (event.key === 'Tab') { setIsOpen(false); return; }

        // El espacio abre, salvo que estemos escribiendo en el buscador
        if (event.key === ' ' && !SHOW_SEARCH) {
            event.preventDefault();
            if (!useIsOpen) return onOpenSelect();
            if (useIndex >= 0) onPick(MEMO__FLAT[useIndex].value);
            return;
        }

        if (!SHOW_SEARCH && event.key.length === 1) onTypeAhead(event.key);
    };

    // ---------------------------- 🔥 NATIVO, si se pide
    if (isNative) {
        const NATIVE_CONTROL = (
            <div className={`${BLOCK}__wrap`}>
                <select id={DOM_ID} name={DOM_NAME}
                    className={`${BLOCK}__input ${BLOCK}__input--select`}
                    value={value ?? ''} onChange={onChange}
                    required={isRequired} disabled={isDisabled}
                    aria-invalid={!!error} {...rest} >
                    {placeholder && <option value="" disabled>{placeholder}</option>}
                    {options.map((opt) => (
                        opt.options
                            ? (
                                <optgroup key={opt.label} label={opt.label}>
                                    {opt.options.map((child) => (
                                        <option key={child.value} value={child.value} disabled={child.isDisabled}>
                                            {child.label}
                                        </option>
                                    ))}
                                </optgroup>
                            )
                            : <option key={opt.value} value={opt.value} disabled={opt.isDisabled}>{opt.label}</option>
                    ))}
                </select>
                <span className={`${BLOCK}__icon ${BLOCK}__icon--end`} aria-hidden="true"><FaChevronDown /></span>
            </div>
        );

        if (isBare) return NATIVE_CONTROL;

        return (
            <FieldShell id={DOM_ID} label={label} hint={hint} error={error}
                isRequired={isRequired} isDisabled={isDisabled} span={span} >
                {NATIVE_CONTROL}
            </FieldShell>
        );
    }

    // ---------------------------- 🔥 PROPIO
    let flatIndex = -1;

    const onRenderOption = (opt) => {

        flatIndex += 1;

        const I = flatIndex;

        return (
            <button key={opt.value} type="button" role="option"
                className={`${BLOCK}__option`}
                data-active={useIndex === I}
                data-selected={String(opt.value) === String(value ?? '')}
                aria-selected={String(opt.value) === String(value ?? '')}
                disabled={opt.isDisabled}
                onMouseEnter={() => setIndex(I)}
                onClick={() => onPick(opt.value)} >

                <span className={`${BLOCK}__option_label`}>
                    <Highlight text={opt.label} query={useQuery} />
                </span>

                <FaCheck className={`${BLOCK}__option_check`} />

            </button>
        );
    };

    const CUSTOM_CONTROL = (
            <div className={`${BLOCK}__select`} ref={REF__useRefBox} data-open={useIsOpen}>

                {/*
                    El <select> real sigue acá para el submit, la
                    validación y los lectores de pantalla.
                */}
                <select className={`${BLOCK}__native`} tabIndex={-1} aria-hidden="true"
                    name={DOM_NAME} value={value ?? ''} onChange={() => { }}
                    required={isRequired} disabled={isDisabled} >
                    <option value="" />
                    {MEMO__FLAT_ALL.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>

                <button ref={REF__useRefButton} type="button" id={DOM_ID}
                    className={`${BLOCK}__input ${BLOCK}__select_button`}
                    data-open={useIsOpen} data-empty={!SELECTED}
                    disabled={isDisabled}
                    aria-haspopup="listbox" aria-expanded={useIsOpen} aria-invalid={!!error}
                    onClick={onToggleSelect} onKeyDown={onKeyDown} >

                    <span className={`${BLOCK}__select_value`}>
                        {SELECTED?.label ?? placeholder}
                    </span>

                    <FaChevronDown className={`${BLOCK}__select_caret`} aria-hidden="true" />

                </button>

                {
                    useIsOpen && (
                        <div className={`${BLOCK}__dropdown`}>

                            {
                                SHOW_SEARCH && (
                                    <div className={`${BLOCK}__dropdown_search`}>

                                        <FaSearch aria-hidden="true" />

                                        <input name={DOM_NAME} autoComplete="nope" data-lpignore="true" data-1p-ignore="true" data-form-type="other" spellCheck={false} autoCorrect="off" autoCapitalize="off" ref={REF__useRefSearchInput} type="text"
                                            value={useQuery}
                                            onChange={(event) => setQuery(event.target.value)}
                                            onKeyDown={onKeyDown}
                                            placeholder={searchPlaceholder}
                                            autoComplete={FIELD_AUTOCOMPLETE_OFF}
                                            aria-label="Buscar en la lista" />

                                        {
                                            useQuery && (
                                                <span className={`${BLOCK}__dropdown_count`}>
                                                    {MEMO__FLAT.length}
                                                </span>
                                            )
                                        }

                                    </div>
                                )
                            }

                            <div className={`${BLOCK}__options`} ref={REF__useRefList} role="listbox" tabIndex={-1}>
                                {
                                    MEMO__FILTERED.length === 0
                                        ? <p className={`${BLOCK}__options_empty`}>{emptyText}</p>
                                        : MEMO__FILTERED.map((group) => (
                                            group.options
                                                ? (
                                                    <div key={group.label} className={`${BLOCK}__optgroup`} role="group">
                                                        <p className={`${BLOCK}__optgroup_label`}>{group.label}</p>
                                                        {group.options.map(onRenderOption)}
                                                    </div>
                                                )
                                                : onRenderOption(group)
                                        ))
                                }
                            </div>

                        </div>
                    )
                }

            </div>
    );

    if (isBare) return CUSTOM_CONTROL;

    return (
        <FieldShell id={DOM_ID} label={label} hint={hint} error={error}
            isRequired={isRequired} isDisabled={isDisabled} span={span} >
            {CUSTOM_CONTROL}
        </FieldShell>
    );
};
